import { type ReactNode } from "react";

// Task #2075 — render a snippet with case-insensitive highlights for each
// query word. Splits the snippet around all matched ranges and wraps matches
// in a <mark> so the host can see exactly which keyword hit. Falls back to
// the plain snippet when there are no words to highlight.
export function highlightSnippet(snippet: string, words: string[]): ReactNode {
  const filtered = words.filter((w) => w.length > 0);
  if (filtered.length === 0) return snippet;
  const lower = snippet.toLowerCase();
  type Range = { start: number; end: number };
  const ranges: Range[] = [];
  for (const w of filtered) {
    const word = w.toLowerCase();
    let from = 0;
    while (true) {
      const idx = lower.indexOf(word, from);
      if (idx === -1) break;
      ranges.push({ start: idx, end: idx + word.length });
      from = idx + word.length;
    }
  }
  if (ranges.length === 0) return snippet;
  ranges.sort((a, b) => a.start - b.start);
  // Merge overlapping ranges so a longer word doesn't double-wrap.
  const merged: Range[] = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) {
      last.end = Math.max(last.end, r.end);
    } else {
      merged.push({ ...r });
    }
  }
  const out: ReactNode[] = [];
  let cursor = 0;
  merged.forEach((r, i) => {
    if (cursor < r.start) out.push(snippet.slice(cursor, r.start));
    out.push(
      <mark key={i} className="rounded bg-yellow-200 px-0.5 text-gray-900">
        {snippet.slice(r.start, r.end)}
      </mark>,
    );
    cursor = r.end;
  });
  if (cursor < snippet.length) out.push(snippet.slice(cursor));
  return out;
}
